import React from 'react'
import { Link } from 'react-router-dom'
import LegalLayout from '../components/LegalLayout'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import StarIcon from '@mui/icons-material/Star'

const plans = [
  {
    name: 'Starter',
    price: '4,999',
    cadence: '/month',
    tagline: 'For individuals, portfolios & landing pages.',
    featured: false,
    features: [
      'Single-page / landing page website',
      'Fully responsive design',
      'Basic on-page SEO setup',
      'Monthly maintenance & minor updates',
      'Email support'
    ]
  },
  {
    name: 'Professional',
    price: '12,999',
    cadence: '/month',
    tagline: 'For growing businesses that need a full web presence.',
    featured: true,
    features: [
      'Up to 5-page custom website',
      'Full-stack MERN development',
      'Database integration',
      'Payment gateway integration',
      'Performance optimization',
      'Priority support'
    ]
  },
  {
    name: 'Enterprise',
    price: '24,999',
    cadence: '/month',
    tagline: 'For scaling products & custom web applications.',
    featured: false,
    features: [
      'Custom web application (unlimited pages)',
      'Admin dashboard & REST APIs',
      'Third-party & API integrations',
      'Monthly analytics & reporting',
      'Dedicated priority support',
      'Ongoing feature development'
    ]
  }
]

const PlanCard = ({ plan }) => (
  <div
    className={`relative rounded-2xl p-[1px] transition-transform duration-300 hover:-translate-y-2 ${
      plan.featured
        ? 'bg-gradient-to-br from-[#2ea759] via-[#34d399] to-[#0ea5e9] shadow-[0_0_40px_-10px_rgba(46,167,89,0.6)]'
        : 'bg-gradient-to-br from-[#1f1f1f] to-[#2a2a2a]'
    }`}
  >
    {plan.featured && (
      <div className='absolute -top-3 left-1/2 -translate-x-1/2 z-10'>
        <span className='flex items-center gap-1 bg-gradient-to-r from-[#2ea759] to-[#0ea5e9] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md'>
          <StarIcon style={{ fontSize: 14 }} /> Most Popular
        </span>
      </div>
    )}

    <div className='h-full rounded-2xl bg-[#0c0c0c] p-8 flex flex-col gap-6'>
        <div className='flex flex-col gap-2'>
            <h3 className='text-2xl font-bold text-white'>{plan.name}</h3>
            <p className='text-[#8f9094] text-sm min-h-[40px]'>{plan.tagline}</p>
        </div>

        <div className='flex items-end gap-1'>
            <span className='text-2xl font-semibold text-[#8f9094] mb-2'>₹</span>
            <span className='text-5xl font-extrabold bg-gradient-to-r from-white to-[#8f9094] bg-clip-text text-transparent'>
              {plan.price}
            </span>
            <span className='text-[#8f9094] mb-2'>{plan.cadence}</span>
        </div>

        <Link
          to='/contact-us'
          className={`text-center font-semibold py-3 rounded-lg transition-all duration-300 ${
            plan.featured
              ? 'bg-gradient-to-r from-[#2ea759] to-[#0ea5e9] text-white hover:opacity-90'
              : 'bg-[#141414] text-white border border-[#2a2a2a] hover:border-[#2ea759]'
          }`}
        >
          Get Started
        </Link>

        <div className='h-[1px] w-full bg-[#1f1f1f]' />

        <ul className='flex flex-col gap-3'>
            {plan.features.map((f, i) => (
              <li key={i} className='flex items-start gap-3 text-[#d4d4d4]'>
                <CheckCircleIcon className='text-[#2ea759] flex-shrink-0' style={{ fontSize: 20, marginTop: 2 }} />
                <span className='text-sm sm:text-base'>{f}</span>
              </li>
            ))}
        </ul>
    </div>
  </div>
)

const Pricing = () => {
  return (
    <LegalLayout wide bare>
        {/* HERO */}
        <div className='flex flex-col items-center text-center gap-4 mb-4'>
            <span className='text-[#2ea759] text-sm font-semibold tracking-widest uppercase'>Plans &amp; Pricing</span>
            <h1 className='text-4xl sm:text-6xl font-extrabold leading-tight'>
              Simple, transparent <br className='hidden sm:block' />
              <span className='bg-gradient-to-r from-[#2ea759] via-[#34d399] to-[#0ea5e9] bg-clip-text text-transparent'>
                pricing for every project
              </span>
            </h1>
            <p className='text-[#8f9094] text-lg max-w-2xl'>
              Choose a monthly plan that fits your needs. All prices are in Indian Rupees (INR) and
              exclusive of applicable taxes. Cancel anytime.
            </p>
        </div>

        {/* PLANS */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-6 items-stretch'>
            {plans.map((plan) => (
              <PlanCard key={plan.name} plan={plan} />
            ))}
        </div>

        {/* CUSTOM / NOTE */}
        <div className='mt-12 rounded-2xl p-[1px] bg-gradient-to-r from-[#1f1f1f] via-[#2ea759]/40 to-[#1f1f1f]'>
            <div className='rounded-2xl bg-[#0c0c0c] px-6 sm:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left'>
                <div className='flex flex-col gap-1'>
                    <h3 className='text-xl sm:text-2xl font-bold text-white'>Need something custom?</h3>
                    <p className='text-[#8f9094]'>
                      One-time projects and tailored scopes are available on request. Get a personalised quote in INR.
                    </p>
                </div>
                <Link
                  to='/contact-us'
                  className='whitespace-nowrap bg-gradient-to-r from-[#2ea759] to-[#0ea5e9] text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity duration-300'
                >
                  Request a Quote
                </Link>
            </div>
        </div>

        <p className='text-center text-[#6b6b6b] text-sm mt-8'>
          Prices may be revised for new orders; confirmed orders are unaffected. Payments are processed
          securely via Razorpay. See our <Link to='/refund-policy' className='text-[#2ea759] hover:underline'>Refund Policy</Link>{' '}
          and <Link to='/terms-and-conditions' className='text-[#2ea759] hover:underline'>Terms &amp; Conditions</Link>.
        </p>
    </LegalLayout>
  )
}

export default Pricing
